import React, { useEffect, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import FlashAlert from "../../Components/FlashAlert/FlashAlert";
import { DeleteIcon, EditIcon } from "../../Components/Icons/ActionIcons";
import apiService from "../../services/api.service";

const emptyForm = () => ({
  username: "",
  password: "",
  first_name: "",
  last_name: "",
  application_id: "",
});

const gestoreLabel = (gestore) => {
  if (!gestore) return "";
  const name = `${gestore.first_name || ""} ${gestore.last_name || ""}`.trim();
  return name || gestore.username;
};

const DashboardGestori = () => {
  const [gestori, setGestori] = useState([]);
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGestore, setEditingGestore] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [gestoreToDelete, setGestoreToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const isEditing = Boolean(editingGestore);

  const appName = (applicationId) => {
    const app = applications.find((item) => String(item.id) === String(applicationId));
    return app ? `${app.name} (/${app.slug})` : "Non assegnato";
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [gestoriRes, appsRes] = await Promise.all([
        apiService.getGestori(),
        apiService.getApplications(),
      ]);
      setGestori(gestoriRes.data || []);
      setApplications(appsRes.data || []);
    } catch {
      setError("Impossibile caricare i gestori.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingGestore(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setError("");
    setSuccess("");
    setEditingGestore(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (gestore) => {
    setError("");
    setSuccess("");
    setEditingGestore(gestore);
    setForm({
      username: gestore.username || "",
      password: "",
      first_name: gestore.first_name || "",
      last_name: gestore.last_name || "",
      application_id: gestore.application_id || "",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      if (isEditing) {
        const payload = {
          username: form.username.trim(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          application_id: Number(form.application_id),
        };
        if (form.password.trim()) {
          payload.password = form.password;
        }
        await apiService.updateGestore(editingGestore.id, payload);
        setSuccess("Gestore aggiornato.");
      } else {
        await apiService.createGestore({
          username: form.username.trim(),
          password: form.password,
          first_name: form.first_name.trim() || null,
          last_name: form.last_name.trim() || null,
          application_id: Number(form.application_id),
        });
        setSuccess("Gestore creato e assegnato all'applicazione.");
      }
      setEditingGestore(null);
      setForm(emptyForm());
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || (isEditing ? "Modifica gestore non riuscita." : "Creazione gestore non riuscita."));
    } finally {
      setSaving(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setGestoreToDelete(null);
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (!gestoreToDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await apiService.deleteGestore(gestoreToDelete.id);
      setSuccess(`Gestore "${gestoreLabel(gestoreToDelete)}" eliminato.`);
      setGestoreToDelete(null);
      await load();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Eliminazione non riuscita.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="dashboard-card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
        <div>
          <h1 className="dashboard-card__title">Gestori</h1>
          <p className="dashboard-card__subtitle">
            Crea, modifica ed elimina i gestori e affidali a un&apos;applicazione.
          </p>
        </div>
        <Button className="users-edit-btn" onClick={openCreate}>
          Nuovo gestore
        </Button>
      </div>

      {error && !showForm && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
      {success && <FlashAlert variant="success" onHidden={() => setSuccess("")}>{success}</FlashAlert>}
      {loading && <p>Caricamento...</p>}

      {!loading && gestori.length === 0 && (
        <Alert variant="info">Nessun gestore creato.</Alert>
      )}

      {!loading && gestori.length > 0 && (
        <div className="dashboard-events-list">
          {gestori.map((gestore) => (
            <div className="dashboard-event-item" key={gestore.id}>
              <div>
                <p className="dashboard-event-item__title">{gestoreLabel(gestore)}</p>
                <p className="dashboard-event-item__meta">{gestore.username}</p>
                <p className="dashboard-event-item__meta">{appName(gestore.application_id)}</p>
              </div>
              <div className="dashboard-event-item__actions">
                <button
                  type="button"
                  className="dashboard-icon-btn"
                  title="Modifica gestore"
                  aria-label={`Modifica ${gestoreLabel(gestore)}`}
                  onClick={() => openEdit(gestore)}
                >
                  <EditIcon />
                </button>
                <button
                  type="button"
                  className="dashboard-icon-btn"
                  title="Elimina gestore"
                  aria-label={`Elimina ${gestoreLabel(gestore)}`}
                  onClick={() => {
                    setSuccess("");
                    setDeleteError("");
                    setGestoreToDelete(gestore);
                  }}
                >
                  <DeleteIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal show={showForm} onHide={closeForm} centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{isEditing ? "Modifica gestore" : "Nuovo gestore"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                required
                value={form.username}
                onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{isEditing ? "Nuova password (opzionale)" : "Password"}</Form.Label>
              <Form.Control
                type="password"
                required={!isEditing}
                minLength={8}
                value={form.password}
                onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                placeholder={isEditing ? "Lascia vuoto per non cambiare" : ""}
                autoComplete={isEditing ? "new-password" : "off"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                value={form.first_name}
                onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cognome</Form.Label>
              <Form.Control
                value={form.last_name}
                onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Applicazione</Form.Label>
              <Form.Select
                required
                value={form.application_id}
                onChange={(e) => setForm((prev) => ({ ...prev, application_id: e.target.value }))}
              >
                <option value="">Seleziona applicazione</option>
                {applications.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name} (/{app.slug})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeForm} disabled={saving}>Annulla</Button>
            <Button type="submit" className="users-edit-btn" disabled={saving || !form.application_id}>
              {saving ? (isEditing ? "Salvataggio..." : "Creazione...") : (isEditing ? "Salva modifiche" : "Crea gestore")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!gestoreToDelete} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Elimina gestore</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError && <FlashAlert variant="danger" onHidden={() => setDeleteError("")}>{deleteError}</FlashAlert>}
          <p>
            Sei sicuro di voler eliminare{" "}
            <strong>{gestoreLabel(gestoreToDelete)}</strong>
            {gestoreToDelete?.username ? ` (${gestoreToDelete.username})` : ""}?
          </p>
          <p className="dashboard-delete-warning">
            L&apos;operazione è irreversibile e rimuoverà anche le presenze
            registrate per questo utente.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={closeDeleteModal} disabled={deleting}>
            Annulla
          </Button>
          <Button className="users-edit-btn" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Eliminazione..." : "Elimina definitivamente"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardGestori;
